/**
 * Какую из подписок клиента показывать.
 *
 * У клиента бывает 2+ подписки в одном боте. Главная умела переключать их лентой
 * чипсов, но выбор жил в `useState` одного экрана: «Устройства», «Улучшить» и
 * «Активность» брали последнюю по сроку и молча показывали чужую подписку
 * (тикет #24, п.10). Теперь выбор общий, живёт в sessionStorage (переживает
 * переходы по табам и перезагрузку мини-аппа, но не новую сессию Telegram) и
 * резолвится по id: протухший id не стирается, а игнорируется, иначе временно
 * пустой кэш затирал бы живой выбор.
 *
 * Search-параметры не подошли: `/activity` это таб нижней навигации, а каждый
 * BackButton ведёт на `/me` без search, выбор терялся бы на каждом прыжке.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Subscription } from '@/api/types';

const STORAGE_KEY = 'vpnf-selected-sub';

let selectedId: string | null = readStored();
const listeners = new Set<() => void>();

function readStored(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function getSelectedSubscriptionId(): string | null {
  return selectedId;
}

export function selectSubscription(id: string | null): void {
  if (id === selectedId) return;
  selectedId = id;
  try {
    if (id) sessionStorage.setItem(STORAGE_KEY, id);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // приватный режим или заблокированное хранилище: выбор живёт до перезагрузки
  }
  listeners.forEach((fn) => fn());
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Чистый резолв: по id, иначе первая в списке (бэк сортирует по `end_date desc`,
 * это и есть прежняя «активная» подписка), иначе null.
 */
export function resolveSelectedSub(
  subs: Subscription[],
  id: string | null,
): Subscription | null {
  if (subs.length === 0) return null;
  if (id) {
    const found = subs.find((s) => s.id === id);
    if (found) return found;
  }
  return subs[0];
}

export function useSelectedSubscription(opts?: { refetchOnMount?: boolean | 'always' }): {
  subs: Subscription[];
  sub: Subscription | null;
  selectedId: string | null;
  select: (id: string) => void;
  isPending: boolean;
  isFetching: boolean;
} {
  const { data, isPending, isFetching } = useQuery({
    queryKey: ['subs'],
    queryFn: () => api.allSubscriptions(),
    placeholderData: keepPreviousData,
    refetchOnMount: opts?.refetchOnMount,
  });
  const id = useSyncExternalStore(subscribe, getSelectedSubscriptionId, getSelectedSubscriptionId);
  const subs = data ?? [];
  const select = useCallback((next: string) => selectSubscription(next), []);
  return {
    subs,
    sub: resolveSelectedSub(subs, id),
    selectedId: id,
    select,
    isPending,
    isFetching,
  };
}
